import { useCallback } from "react";

import { getFilterValue } from "../utils/getFilterValue";
import { isEmptyValue } from "../utils/isEmptyValue";
import { isNumeric } from "../../../utils/string";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { useWidgetConfiguration } from "../../Dashboard/hooks/useWidgetConfiguration";
import { FilterItem, WidgetType } from "../types";

const getMinMaxFromStringValue = (
  items: FilterItem[],
  value: { min: string; max: string },
  current: FilterItem,
  type: "min" | "max",
) => {
  const valueIndex = items.findIndex(item => item.value === (type === "min" ? value.min : value.max));
  const currentIndex = items.findIndex(item => item.value === (type === "min" ? value.min : value.max));

  return type === "min"
    ? currentIndex < valueIndex
      ? current.min
      : value.min
    : currentIndex >= valueIndex
    ? current.max
    : value.max;
};

export const useFeatureFilters = (type: WidgetType, filterName: string, items?: FilterItem[]) => {
  const { filters, changeFilters } = useWidgetContext(type);
  const { currentConfig } = useWidgetConfiguration(type);

  const hasAnyFilter = Array.isArray(filters[filterName]?.value)
    ? !!(filters[filterName].value as string[]).length
    : !!filters[filterName]?.value;

  const isFiltered = useCallback(
    (name: string) => {
      return (
        Array.isArray(filters[filterName]?.value) &&
        (filters[filterName].value as string[]).length &&
        (filters[filterName].value as string[]).includes(name)
      );
    },
    [filterName, filters],
  );

  const onFilter = useCallback(
    (newValue: string) => {
      const value = getFilterValue({
        selectedFilters: filters,
        configFilters: currentConfig.filters,
        filterName,
        newValue,
      });

      const data = items?.filter(item => value.includes(item.name));
      const { min, max } =
        data?.reduce(
          (result, current) => {
            const isNumericValue = isNumeric(current.min) && isNumeric(current.max);
            const currentMin = isNumericValue ? Number(current.min) : current.min;
            const currentMax = isNumericValue ? Number(current.max) : current.max;

            return {
              min:
                result.min === null
                  ? isEmptyValue(current.min)
                    ? null
                    : currentMin
                  : isNumericValue
                  ? Math.min(result.min, currentMin as number)
                  : getMinMaxFromStringValue(items, result, current, "min"),
              max:
                result.max === null
                  ? isEmptyValue(current.max)
                    ? null
                    : currentMax
                  : isNumericValue
                  ? Math.max(result.max, currentMax as number)
                  : getMinMaxFromStringValue(items, result, current, "max"),
            };
          },
          { min: null, max: null },
        ) || {};

      changeFilters({ [filterName]: { value, min, max } });
    },
    [filters, currentConfig.filters, filterName, items, changeFilters],
  );

  return { filters, hasAnyFilter, isFiltered, onFilter };
};
